import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie-based auth: httpOnly cookie is sent automatically with same-origin
// requests. No tokens are stored in localStorage (XSS-safe).
const api = axios.create({ baseURL: API, withCredentials: true });

// Tracks whether a real authenticated session is active (kept in sync by AuthContext).
// Lets the 401 handler distinguish "a live session was just revoked" (bounce to sign-in)
// from "anonymous request on a public page" (let the component show a gentle prompt).
let sessionActive = false;
export function setSessionActive(v) { sessionActive = v; }

// Surface rate-limit (429) responses with a friendly message, and cleanly send a user
// back to sign-in when their live session is invalidated mid-flight (e.g., token revoked
// / M1 forced re-login) so an involuntary 401 never leaves them stuck.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const reqUrl = error.config?.url || "";
    const isBackgroundGet = (error.config?.method || "get").toLowerCase() === "get" || reqUrl.includes("/auth/me") || reqUrl.includes("/track");
    if (status === 429 && !isBackgroundGet) {
      const retry = error.response.headers?.["retry-after"];
      toast.error(
        retry
          ? `You're going too fast. Please wait ${retry}s and try again.`
          : "Too many requests. Please slow down and try again shortly."
      );
    } else if (status === 401) {
      const url = error.config?.url || "";
      const isAuthCall = url.includes("/auth/");
      const onLogin = typeof window !== "undefined" && window.location.pathname === "/login";
      if (sessionActive && !isAuthCall && !onLogin) {
        sessionActive = false;
        toast.error("Your session has ended. Please sign in again.");
        setTimeout(() => window.location.assign("/login"), 400);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
